/**
 * 已签合同转设计队列。
 *
 * 业务背景：同一客户可能先后签署多份合同，而一个 CRM 客户下可能已有旧设计项目。
 * 旧流程只按 leadId 幂等，后续合同会复用那条项目并继承其逾期节点。现在把每份上传合同
 * 作为独立转设计记录，由设计部内部操作人明确选择新建项目，或关联该客户的现有设计项目；
 * 客户不参与项目选择，未关联前合同仍留在此队列中供设计部处理。
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Download, FileSignature, Loader2, Link2, Plus, RefreshCw } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { toast } from "../../../components/ui/sonner";
import { useApp } from "../../../context/AppContext";
import { downloadFile } from "../../../lib/fileDownload";
import { fmtD } from "../../../lib/usDate";

export default function DesignContractHandoff({ api, query = "", onChanged }) {
  const { lang } = useApp();
  const zh = lang === "zh";
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const { data } = await api.get("/design/v3/contracts/pending");
      setRows(data?.contracts || []);
    } catch (e) {
      setError(e?.detailText || e?.response?.data?.detail || e.message || (zh ? "加载失败" : "Could not load contracts"));
    }
  }, [api, zh]);
  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const q = String(query || "").trim().toLowerCase();
    if (!q) return rows || [];
    return (rows || []).filter((r) => [r.clientName, r.address, r.contractName, r.contractId, r.projectId]
      .some((v) => String(v || "").toLowerCase().includes(q)));
  }, [rows, query]);

  const assign = async (row, projectMode, projectId = "") => {
    const key = row.contractId;
    setBusy(key);
    try {
      const body = { contractId: key, projectMode };
      if (projectId) body.projectId = projectId;
      const { data } = await api.post("/design/v3/from-lead/" + encodeURIComponent(row.leadId), body);
      toast.success(projectMode === "new"
        ? (zh ? "已为合同新建设计项目 " + (data?.projectId || "") : "Created design project " + (data?.projectId || ""))
        : (zh ? "合同已关联设计项目 " + (data?.projectId || "") : "Contract linked to " + (data?.projectId || "")));
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.detailText || e?.response?.data?.detail || e.message || (zh ? "操作失败" : "Action failed"));
    } finally {
      setBusy("");
    }
  };

  const downloadContract = (row) => downloadFile(row.fileId, row.contractName || "SignedContract.pdf", { orig: true })
    .catch((e) => toast.error(e?.message || (zh ? "合同下载失败，请重试" : "Contract download failed")));

  return (
    <section className="space-y-3" data-testid="dv3-contract-handoff">
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5">
        <FileSignature className="h-4 w-4 text-brand-600" />
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-slate-800">{zh ? "待转设计合同" : "Contracts awaiting design handoff"}</h2>
          <p className="text-xs text-slate-500">{zh ? "每份合同单独选择新建项目或关联该客户的现有项目。" : "Choose a new or existing design project for each contract."}</p>
        </div>
        <span className="text-xs tabular-nums text-slate-500" data-testid="dv3-contract-handoff-count">{rows?.length ?? "—"}</span>
        <Button type="button" size="sm" variant="outline" onClick={load} disabled={busy !== ""} data-testid="dv3-contract-handoff-refresh">
          <RefreshCw className="mr-1 h-3.5 w-3.5" />{zh ? "刷新" : "Refresh"}
        </Button>
      </div>

      {error && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert" data-testid="dv3-contract-handoff-error">
          <span className="min-w-0 flex-1">{error}</span>
          <Button type="button" size="sm" variant="outline" onClick={load} data-testid="dv3-contract-handoff-retry">{zh ? "重试" : "Retry"}</Button>
        </div>
      )}

      {!rows && !error && <div className="flex h-28 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-400" role="status"><Loader2 className="h-4 w-4 animate-spin" />{zh ? "加载合同…" : "Loading contracts…"}</div>}
      {rows && !error && visible.length === 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500" data-testid="dv3-contract-handoff-empty">
          {query ? (zh ? "没有匹配的待转设计合同" : "No contracts match this search") : (zh ? "当前没有待分配的签约合同" : "No signed contracts are waiting for design handoff")}
        </div>
      )}
      {visible.map((row) => {
        const rowBusy = busy === row.contractId;
        const anyBusy = busy !== "";
        const existing = row.existingProjects || [];
        return (
          <article key={row.contractId} className="grid gap-3 rounded-lg border border-slate-200 bg-white p-3 md:grid-cols-[minmax(0,1fr)_minmax(260px,0.9fr)] md:items-center" data-testid={"dv3-contract-handoff-row-" + row.contractId}>
            <div className="min-w-0 space-y-1">
              <p className="truncate text-sm font-semibold text-slate-800" title={row.clientName || ""}>{row.clientName || (zh ? "未命名客户" : "Unnamed customer")}</p>
              <p className="truncate text-xs text-slate-600" title={row.address || ""}>{row.address || (zh ? "未登记地址" : "No address")}</p>
              <p className="truncate text-xs text-slate-500" title={row.contractName || ""}>
                <FileSignature className="mr-1 inline h-3.5 w-3.5 text-slate-400" />{row.contractName || (zh ? "签约合同 PDF" : "Signed contract PDF")}
                {/* 【2026-10-29 时区统一】原为 `String(row.uploadedAt).slice(0,10)`（UTC 日），
                    与设计部其他位置（洛杉矶日历日）会差一天，改用 fmtD 统一口径。 */}
                {row.uploadedAt ? <span className="ml-2 text-slate-400">{fmtD(row.uploadedAt)}</span> : null}
              </p>
              {row.fileId && <Button type="button" size="sm" variant="ghost" onClick={() => downloadContract(row)} className="h-7 px-1.5 text-xs text-brand-700" data-testid={"dv3-contract-download-" + row.contractId}>
                <Download className="mr-1 h-3.5 w-3.5" />{zh ? "下载合同 PDF" : "Download contract PDF"}
              </Button>}
              {row.uploadedBy && <p className="text-[11px] text-slate-400">{zh ? "上传人" : "Uploaded by"} · {row.uploadedBy}</p>}
              {row.assignedSales && <p className="text-[11px] text-slate-400">{zh ? "负责销售" : "Sales"} · {row.assignedSales}</p>}
            </div>
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <Button type="button" size="sm" disabled={anyBusy} onClick={() => assign(row, "new")} className="bg-brand-600 text-white hover:bg-brand-700" data-testid={"dv3-contract-create-project-" + row.contractId}>
                {rowBusy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Plus className="mr-1 h-3.5 w-3.5" />}{zh ? "新建设计项目" : "Create design project"}
              </Button>
              {existing.length > 0 ? (
                <>
                  <label className="sr-only" htmlFor={"dv3-contract-project-select-" + row.contractId}>{zh ? "关联现有设计项目" : "Choose an existing design project"}</label>
                  <select id={"dv3-contract-project-select-" + row.contractId} value={selected[row.contractId] || ""} disabled={anyBusy}
                    onChange={(e) => setSelected((p) => ({ ...p, [row.contractId]: e.target.value }))}
                    className="h-9 min-w-0 flex-1 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                    data-testid={"dv3-contract-project-select-" + row.contractId}>
                    <option value="">{zh ? "选择该客户的现有项目" : "Choose an existing project"}</option>
                    {existing.map((p) => <option key={p.id} value={p.id}>{p.projectId} · {p.address || p.workStatusName || ""}</option>)}
                  </select>
                  <Button type="button" size="sm" variant="outline" disabled={anyBusy || !selected[row.contractId]}
                    onClick={() => assign(row, "existing", selected[row.contractId])} data-testid={"dv3-contract-link-project-" + row.contractId}>
                    <Link2 className="mr-1 h-3.5 w-3.5" />{zh ? "关联现有" : "Link existing"}
                  </Button>
                </>
              ) : <span className="text-xs text-slate-400">{zh ? "该客户暂无可关联的设计项目" : "No existing design project for this customer"}</span>}
            </div>
          </article>
        );
      })}
    </section>
  );
}
